// frontend/src/components/Chat/MessageInput.jsx
// Поле ввода, вложения, emoji и управление голосовой записью.

import React, { useRef, useState } from "react";

import useMatrixMessageSender from "../hooks/useMatrixMessageSender";
import useMatrixVoiceRecorder from "../hooks/useMatrixVoiceRecorder";

import "./MessageInput.css";

const EMOJIS = [
  "😀",
  "😂",
  "😍",
  "🥰",
  "😎",
  "🤔",
  "😅",
  "😭",
  "😡",
  "👍",
  "👎",
  "👏",
  "🙏",
  "🔥",
  "❤️",
  "💯",
  "🎉",
  "🚀",
  "✅",
  "❌",
];

export default function MessageInput({
  roomId,
  client,
}) {
  const [value, setValue] = useState("");
  const [emojiOpen, setEmojiOpen] =
    useState(false);
  const [fileOpen, setFileOpen] =
    useState(false);

  const textareaRef =
    useRef(null);

  const imageInputRef =
    useRef(null);

  const videoInputRef =
    useRef(null);

  const fileInputRef =
    useRef(null);

  /*
   * Отправка обычных сообщений
   * и файлов.
   */
  const messageSender =
    useMatrixMessageSender(
      client,
      roomId
    );

  const sending =
    messageSender.sending;

  const fileUploading =
    messageSender.uploading;

  const sendError =
    messageSender.error;

  const sendText =
    messageSender.sendText;

  const sendFileMessage =
    messageSender.sendFileMessage;

  const clearSendError =
    messageSender.clearError;

  /*
   * Запись голосовых сообщений.
   */
  const voiceRecorder =
    useMatrixVoiceRecorder(
      client,
      roomId
    );

  const recording =
    voiceRecorder.recording;

  const voiceUploading =
    voiceRecorder.uploading;

  const voiceError =
    voiceRecorder.error;

  const startRecording =
    voiceRecorder.startRecording;

  const stopRecording =
    voiceRecorder.stopRecording;

  const clearVoiceError =
    voiceRecorder.clearError;

  /*
   * Общее состояние загрузки.
   */
  const uploading =
    fileUploading ||
    voiceUploading;

  /*
   * Общая ошибка.
   */
  const error =
    sendError ||
    voiceError;

  /*
   * Заблокировано ли поле.
   */
  const disabled =
    sending ||
    uploading ||
    recording;

  /*
   * Отправка текста.
   */
  async function handleSubmit(event) {
    event.preventDefault();

    const text =
      value.trim();

    if (!text) {
      return;
    }

    if (sending) {
      return;
    }

    if (uploading) {
      return;
    }

    if (recording) {
      return;
    }

    try {
      clearSendError();
      clearVoiceError();

      await sendText(text);

      setValue("");

      textareaRef.current?.focus();
    } catch {
      /*
       * Ошибка уже записана
       * внутри useMatrixMessageSender.
       */
    }
  }

  /*
   * Enter = отправить.
   * Shift + Enter = новая строка.
   */
  function handleKeyDown(event) {
    if (
      event.key === "Enter" &&
      !event.shiftKey
    ) {
      event.preventDefault();

      handleSubmit(event);
    }
  }

  /*
   * Добавление emoji.
   */
  function addEmoji(emoji) {
    setValue(
      (current) =>
        `${current}${emoji}`
    );

    setEmojiOpen(false);

    textareaRef.current?.focus();
  }

  /*
   * Выбор файла.
   */
  async function handleFileChange(
    event
  ) {
    const file =
      event.target.files?.[0];

    if (!file) {
      return;
    }

    try {
      clearSendError();
      clearVoiceError();

      await sendFileMessage(file);
    } catch {
      /*
       * Ошибка уже записана
       * внутри useMatrixMessageSender.
       */
    }

    /*
     * Позволяет выбрать тот же файл
     * повторно.
     */
    event.target.value = "";
  }

  /*
   * Открыть выбор изображения.
   */
  function openImagePicker() {
    setFileOpen(false);

    imageInputRef.current?.click();
  }

  /*
   * Открыть выбор видео.
   */
  function openVideoPicker() {
    setFileOpen(false);

    videoInputRef.current?.click();
  }

  /*
   * Открыть выбор обычного файла.
   */
  function openFilePicker() {
    setFileOpen(false);

    fileInputRef.current?.click();
  }

  /*
   * Изменение текста.
   */
  function handleChange(event) {
    setValue(event.target.value);

    clearSendError();
    clearVoiceError();
  }

  return (
    <div className="chat-input">
      {error && (
        <div className="chat-input__error">
          {error}
        </div>
      )}

      <div className="chat-input__toolbar">

        {/* =========================
            EMOJI
        ========================== */}

        <div className="chat-input__emoji-wrapper">
          <button
            type="button"
            className="chat-input__tool"
            onClick={() =>
              setEmojiOpen(
                (current) =>
                  !current
              )
            }
            disabled={disabled}
            aria-label="Смайлики"
          >
            😊
          </button>

          {emojiOpen && (
            <div className="chat-input__emoji-picker">
              {EMOJIS.map(
                (emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() =>
                      addEmoji(
                        emoji
                      )
                    }
                  >
                    {emoji}
                  </button>
                )
              )}
            </div>
          )}
        </div>

        {/* =========================
            ФАЙЛЫ
        ========================== */}

        <div className="chat-input__file-wrapper">
          <button
            type="button"
            className="chat-input__tool"
            onClick={() =>
              setFileOpen(
                (current) =>
                  !current
              )
            }
            disabled={disabled}
            aria-label="Вложения"
          >
            📎
          </button>

          {fileOpen && (
            <div className="chat-input__file-menu">

              <button
                type="button"
                onClick={
                  openImagePicker
                }
              >
                🖼️ Фото
              </button>

              <button
                type="button"
                onClick={
                  openVideoPicker
                }
              >
                🎥 Видео
              </button>

              <button
                type="button"
                onClick={
                  openFilePicker
                }
              >
                📎 Файл
              </button>

            </div>
          )}
        </div>

        {/* Скрытые input для файлов */}

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={
            handleFileChange
          }
        />

        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          hidden
          onChange={
            handleFileChange
          }
        />

        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={
            handleFileChange
          }
        />

        {/* =========================
            VOICE
        ========================== */}

        <button
          type="button"
          className={
            recording
              ? [
                  "chat-input__tool",
                  "chat-input__voice",
                  "chat-input__voice--recording",
                ].join(" ")
              : [
                  "chat-input__tool",
                  "chat-input__voice",
                ].join(" ")
          }
          onClick={
            recording
              ? stopRecording
              : startRecording
          }
          disabled={
            sending ||
            uploading
          }
          aria-label={
            recording
              ? "Остановить запись"
              : "Записать голосовое"
          }
        >
          {recording
            ? "⏹️"
            : "🎙️"}
        </button>

        {/* =========================
            TEXT INPUT
        ========================== */}

        <form
          className="chat-input__form"
          onSubmit={
            handleSubmit
          }
        >
          <textarea
            ref={textareaRef}
            value={value}
            onChange={
              handleChange
            }
            onKeyDown={
              handleKeyDown
            }
            placeholder={
              recording
                ? "Идёт запись..."
                : uploading
                ? "Загрузка..."
                : "Написать сообщение..."
            }
            rows={1}
            disabled={
              !client ||
              !roomId ||
              disabled
            }
          />

          <button
            type="submit"
            disabled={
              !client ||
              !roomId ||
              !value.trim() ||
              disabled
            }
            aria-label="Отправить сообщение"
          >
            {sending
              ? "..."
              : "➤"}
          </button>
        </form>
      </div>
    </div>
  );
}